/**
 * The back office's menu: a handful of areas, each opening onto its screens.
 *
 * Features group their own entries — `clients:group`, `orders:group` — and a
 * reader does not think in features. "Ventes" is customers, account requests,
 * orders and invoices, which four packages own; only the composition root may
 * know all four. So the areas are declared in this app (`nav-areas.ts`), and
 * everything else still comes from the route registry: labels, order, and
 * the permission each entry needs. An area can list a destination but never
 * invent one, and a group no area takes is shown under its own heading rather
 * than lost — `arrangeNavigation` in `core-router`.
 */

import { useState, type ReactNode } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import {
  arrangeNavigation,
  currentEntry,
  type NavNode,
  type NavRoute,
} from "@hardauto/core-router";
import { NavGroup } from "@hardauto/core-ui";

import { icons } from "./icons";
import { NAV_AREAS, type BackOfficeArea } from "./nav-areas";
import "./i18n";

type Node = NavNode<BackOfficeArea>;

function keyOf(node: Node): string {
  if (node.kind === "area") return node.area.id;
  if (node.kind === "group") return node.group;
  return node.entry.path;
}

function entriesOf(node: Node): readonly NavRoute[] {
  return node.kind === "entry" ? [node.entry] : node.entries;
}

function EntryLink({ route, icon }: { route: NavRoute; icon?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <Link
      to={route.path}
      className="hardauto-nav__link"
      // `exact`, because every entry here is a page rather than a section.
      // TanStack marks a link active by PREFIX by default, which left `/admin`
      // announced as the current page while the user was on `/admin/audit` —
      // two "current page" entries at once, which is worse than none for
      // anyone relying on the announcement.
      activeOptions={{ exact: true }}
      // Announced, not merely styled: a screen-reader user has no other way
      // to know which page they are on.
      activeProps={{ "aria-current": "page" }}
    >
      {icon ? (
        <span className="hardauto-nav__icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className="hardauto-nav__label">{t(route.nav.labelKey)}</span>
    </Link>
  );
}

export interface RailNavigationProps {
  /** Every entry the principal may reach, already filtered. */
  entries: readonly NavRoute[];
}

export function RailNavigation({ entries }: RailNavigationProps) {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  const nodes = arrangeNavigation(entries, NAV_AREAS);
  const here = currentEntry(entries, pathname);
  const hereNode = here ? nodes.find((node) => entriesOf(node).includes(here)) : undefined;
  const currentKey = hereNode ? keyOf(hereNode) : null;

  // One area open at a time — the rail stays short enough to read, which is
  // the point of grouping it. Arriving in an area opens it; between arrivals
  // the reader decides. Adjusted during render rather than in an effect, so
  // the rail never paints one frame with the previous area open.
  const [open, setOpen] = useState<string | null>(currentKey);
  const [openedFor, setOpenedFor] = useState<string | null>(currentKey);
  if (openedFor !== currentKey) {
    setOpenedFor(currentKey);
    setOpen(currentKey);
  }

  return (
    <ul className="hardauto-nav">
      {nodes.map((node) => {
        const key = keyOf(node);
        const routes = entriesOf(node);
        const icon = node.kind === "area" ? node.area.icon : icons.other;

        // A heading over a single screen is a click for nothing: an area the
        // principal reaches one screen of is that screen.
        const only = routes.length === 1 ? routes[0] : undefined;
        if (only) {
          return (
            <li key={key}>
              <EntryLink route={only} icon={icon} />
            </li>
          );
        }

        return (
          <li key={key}>
            <NavGroup
              label={t(node.kind === "area" ? node.area.labelKey : key)}
              icon={icon}
              open={open === key}
              current={currentKey === key}
              onToggle={() => {
                setOpen(open === key ? null : key);
              }}
            >
              {routes.map((route) => (
                <li key={route.path}>
                  <EntryLink route={route} />
                </li>
              ))}
            </NavGroup>
          </li>
        );
      })}
    </ul>
  );
}
